import React from "react";
import {
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getColorForSpecies } from "../colors";

const formatWrPercent = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;

export default function AvgWrVsDateChart({ graphData, isMobile }) {
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };

// First, sort the data by date
const sortedByDate = [...graphData].sort((a, b) => new Date(a.date) - new Date(b.date));

// Group by date and species, taking the latest entry for each combination
const dateSpeciesGroups = {};
sortedByDate.forEach((item) => {
  if (item.count > 0) {
    const key = `${item.date}-${item.species || "Combined"}-${item.isStocking ? "stk" : "spl"}`;
    dateSpeciesGroups[key] = {
      date: item.date,
      species: item.species || "Combined",
      averageWr: item.averageWr,
      stdev: item.stdev,
      count: item.count,
      isStocking: !!item.isStocking,
    };
  }
});

// Convert to array and sort by date
const processedData = Object.values(dateSpeciesGroups)
  .sort((a, b) => new Date(a.date) - new Date(b.date));

// Get unique species that have data
const uniqueSpeciesDate = [...new Set(processedData
  .filter(d => d.count > 0)
  .map(d => d.species))];

// Build Y-axis ticks like first graph (include 1.0; 0 start; step 0.2/0.4)
const yValuesDate = processedData.map(d => Number(d.averageWr || 0));
const yMaxValDate = yValuesDate.length ? Math.max(...yValuesDate) : 0;
let yStepDate = 0.2;
const yCandidateUpperDate = yMaxValDate + 0.1;
let estimatedCountDate = Math.ceil(yCandidateUpperDate / yStepDate) + 1;
if (estimatedCountDate > 12) {
  yStepDate = 0.4;
}
const minTicksDate = 10;
let yUpperDate = Math.max(yCandidateUpperDate, (minTicksDate - 1) * yStepDate);
yUpperDate = Math.ceil(yUpperDate / yStepDate) * yStepDate;
let yTicksDate = [];
for (let v = 0; v <= yUpperDate + 1e-9; v += yStepDate) yTicksDate.push(Number(v.toFixed(2)));
if (!yTicksDate.includes(1)) yTicksDate.push(1);
yTicksDate = Array.from(new Set(yTicksDate)).sort((a, b) => a - b);

return (
  <ResponsiveContainer {...commonProps}>
    <ComposedChart data={processedData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
      <XAxis
        dataKey="date"
        label={{
          value: "Date",
          position: "outsideBottom",
          textAnchor: "middle",
          offset: isMobile ? 10 : 20,
          fontSize: isMobile ? 16 : 22,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        angle={0}
        tickCount={20}
        textAnchor="middle"
        height={isMobile ? 80 : 60}
        tick={{ fontSize: isMobile ? 14 : 16, fontWeight: 'bold', fill: '#000000' }}
        type="category"
        allowDuplicatedCategory={false}
        stroke="#000000"
        strokeWidth={2}
      />
      <YAxis
        label={{
          value: "Avg Relative Weight (Wr %)",
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 16 : 22,
          fontWeight: 'bold',
          fill: '#000000'
        }}
        domain={[0, yTicksDate[yTicksDate.length - 1] || 0]}
        ticks={yTicksDate}
        tickFormatter={formatWrPercent}
        tick={{ fontSize: isMobile ? 14 : 16, fontWeight: 'bold', fill: '#000000' }}
        stroke="#000000"
        strokeWidth={2}
      />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;

          // Filter out species with no data
          const validPayload = payload.filter(p => p.payload.count > 0);
          if (validPayload.length === 0) return null;

          // Totals across species for this date
          const totalCount = validPayload.reduce((sum, entry) => sum + (entry.payload.count || 0), 0);
          const weightedAvgWr = totalCount > 0
            ? validPayload.reduce((sum, entry) => sum + (entry.payload.averageWr * entry.payload.count), 0) / totalCount
            : 0;

          return (
            <div className="custom-tooltip" style={{
              background: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              padding: '12px',
              fontSize: isMobile ? '12px' : '14px',
              minWidth: '220px'
            }}>
              <div style={{
                borderBottom: '1px solid #eee',
                marginBottom: '8px',
                paddingBottom: '4px'
              }}>
                <p style={{
                  margin: '0',
                  fontWeight: 'bold',
                  color: '#333'
                }}>
                  Date: {label}
                </p>
              </div>

              <div style={{ display: 'grid', gap: '12px' }}>
                {validPayload.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      borderLeft: `3px solid ${entry.color}`,
                      paddingLeft: '8px'
                    }}
                  >
                    <div style={{
                      fontWeight: 'bold',
                      color: entry.color,
                      marginBottom: '4px'
                    }}>
                      {entry.name.split(' (')[0]}:
                    </div>
                    <div style={{
                      display: 'grid',
                      gap: '2px',
                      fontSize: isMobile ? '11px' : '13px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Avg Wr:</span>
                        <span style={{
                          fontWeight: 'bold',
                          color: entry.payload.averageWr >= 1.0 ? '#27ae60' : '#c0392b'
                        }}>
                          {formatWrPercent(entry.payload.averageWr)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>StdDev:</span>
                        <span>{entry.payload.stdev?.toFixed(2) || "N/A"}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Count:</span>
                        <span>{entry.payload.count}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Overall totals across species */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                    <span>Total Count:</span>
                    <span>{totalCount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Overall Avg Wr:</span>
                    <span style={{ fontWeight: 'bold', color: weightedAvgWr >= 1.0 ? '#27ae60' : '#c0392b' }}>
                      {formatWrPercent(weightedAvgWr)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Legend
        layout="horizontal"
        verticalAlign="top"
        align="center"
        wrapperStyle={{
          paddingBottom: isMobile ? 20 : 40,
          fontSize: isMobile ? 12 : 14,
        }}
      />
      <ReferenceLine
        y={1.0}
        stroke="#e74c3c"
        strokeWidth={2}
        strokeDasharray="5 5"
      />
      {uniqueSpeciesDate.map((species) => {
        const speciesColor = getColorForSpecies(
          species,
          uniqueSpeciesDate
        );
        const speciesData = processedData.filter(
          (d) => d.species === species && d.count > 0
        );
        if (speciesData.length === 0) return null;

        const samp = speciesData.filter((d) => !d.isStocking);
        const stock = speciesData.filter((d) => d.isStocking);
        const totalCount = samp.reduce((sum, d) => sum + (d.count || 0), 0);

        return (
          <React.Fragment key={species}>
            {samp.length > 0 && (
              <Line
                type="linear"
                data={samp}
                dataKey="averageWr"
                stroke={speciesColor}
                strokeWidth={2}
                dot={{ fill: speciesColor, strokeWidth: 2, r: 4 }}
                name={`${species} (${totalCount})`}
              >
                <ErrorBar dataKey="stdev" width={4} stroke={speciesColor} />
              </Line>
            )}
            {stock.length > 0 && (
              <Scatter
                name={`${species} (stocking) (${stock.reduce((sum, d) => sum + (d.count || 0), 0)})`}
                data={stock}
                dataKey="averageWr"
                fill={speciesColor}
                shape="diamond"
              />
            )}
          </React.Fragment>
        );
      })}
    </ComposedChart>
  </ResponsiveContainer>
);
}
